import { Link } from "@tanstack/react-router";
import { Phone, MessageCircle, Calendar, ArrowRight } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";

interface Props {
  /** Which page this CTA appears on — passed into tracking. */
  page: string;
  /** Service slug (mot, servicing, diagnostics, brakes, etc.). */
  service?: string;
  /** Optional area for tracking attribution. */
  area?: string;
  /** Booking context prefilled into the booking notes. */
  bookingContext?: string;
  /** Headline override. */
  headline?: string;
  /** Subtext override. */
  subtext?: string;
  /** When true, includes the PCO/private hire link. */
  showPcoLink?: boolean;
}

/**
 * Compact "Need MOT or driver support?" CTA used across service and cost pages
 * to boost private-hire / high-mileage driver enquiries. Tracks
 * `driver_support_cta_click` with service + page + area metadata.
 */
export function DriverSupportCTA({
  page,
  service,
  area,
  bookingContext,
  headline,
  subtext,
  showPcoLink = true,
}: Props) {
  const biz = getBusinessConfig();
  const meta: Record<string, string> = { location: "driver_support_cta", page };
  if (service) meta.service = service;
  if (area) meta.area = area;
  const bookSearch: Record<string, string> = {};
  if (service) bookSearch.service = service;
  if (area) bookSearch.area = area;
  const bookHref = bookingContext
    ? `/book?${new URLSearchParams({ ...bookSearch, context: bookingContext }).toString()}`
    : `/book?${new URLSearchParams(bookSearch).toString()}`;
  const waText = `I'm enquiring about ${service ?? "garage support"}${
    bookingContext ? ` (${bookingContext})` : ""
  } — please get back to me. Reg: `;
  const wa = `https://wa.me/${biz.phone.replace(/\D/g, "")}?text=${encodeURIComponent(waText)}`;

  return (
    <aside className="my-10 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 md:p-7">
      <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
            Need MOT or driver support?
          </p>
          <h3 className="mt-2 text-lg md:text-xl font-bold text-foreground leading-snug">
            {headline ?? "Driving for work or covering high mileage?"}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            {subtext ??
              "Call Woodseer Autos for MOT, servicing, diagnostics and repair support in East London — clear advice before any work starts."}
          </p>
          {showPcoLink && (
            <Link
              to="/pco-mot-east-london"
              onClick={() => trackEvent("driver_support_cta_click", { ...meta, action: "pco_link" })}
              className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
            >
              PCO / private-hire driver support <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
        <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[220px]">
          <a
            href={biz.phoneHref}
            onClick={() => trackEvent("driver_support_cta_click", { ...meta, action: "call" })}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
          >
            <Phone className="h-4 w-4" /> Call {biz.phone}
          </a>
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("driver_support_cta_click", { ...meta, action: "whatsapp" })}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-3 text-sm font-bold text-white hover:bg-[#1ea952]"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <a
            href={bookHref}
            onClick={() => trackEvent("driver_support_cta_click", { ...meta, action: "book" })}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-bold text-foreground hover:border-primary/40"
          >
            <Calendar className="h-4 w-4" /> Request MOT booking
          </a>
        </div>
      </div>
    </aside>
  );
}
